import { plainToInstance, Type } from 'class-transformer'
import moment from 'moment'
import { Entity } from '@core/entities/entity'
import { ITaskBase } from './ITaskBase'
import {
  DueDateConfig,
  TaskTemplatePriorityType,
} from '@core/entities/task/TaskTemplateProperties'
import { User } from '@core/entities/user'
import { TaskStep, TaskStepsWrapper } from '@core/entities/task/TaskStep'
import { TemplateForm } from '@core/entities/template/TemplateForm'
import { ChangeRequest } from '@core/entities/permit/ChangeRequest'
import { statuses, StatusColor } from '@core/theme/statuses'
import { Project } from '@core/entities/project/Project'
import { TaskPermittedActions, TaskSource, TaskStatus } from './types'
import { TemplateFormAnswers } from '@core/entities/template/TemplateFormAnswers'
import { TaskAttachmentsWrapper } from '@core/entities/task/TaskAttachment'
import { TaskTemplate } from '@core/entities/task/TaskTemplate'
import { ImageWarning } from '@core/entities/ImageWarning'

export class TaskBase<
  ExtendedType extends ITaskBase = ITaskBase,
> extends Entity<ExtendedType> {
  id: number

  organisationId?: number

  summary: string

  priority: TaskTemplatePriorityType

  dueDate: string

  status: TaskStatus

  source: TaskSource | null

  @Type(() => TaskStep)
  currentStep: TaskStep

  @Type(() => TaskStep)
  nextStep: TaskStep

  @Type(() => TaskStepsWrapper)
  submittedSteps: TaskStepsWrapper

  @Type(() => User)
  assignee: User

  @Type(() => TaskAttachmentsWrapper)
  attachments?: TaskAttachmentsWrapper

  @Type(() => User)
  createdBy: User

  @Type(() => User)
  assigneeUser: User

  createdOn: string

  modifiedOn: string

  @Type(() => ChangeRequest)
  lastRequestedChange?: ChangeRequest

  permittedActions: Array<TaskPermittedActions>

  @Type(() => Project)
  project: Project

  location?: string

  @Type(() => TaskTemplate)
  template: TaskTemplate

  @Type(() => ImageWarning)
  warnings: ImageWarning[]

  public static new(payload: unknown): TaskBase {
    const entity = plainToInstance(TaskBase, payload)

    return entity
  }

  public static calculateDueDate({
    rawDate,
    priority,
    dueDateConfig,
  }: {
    rawDate?: moment.Moment
    priority: TaskTemplatePriorityType | undefined
    dueDateConfig: DueDateConfig | undefined
  }): string {
    const { prioritySetsTheDueDateConfig } = dueDateConfig
    // 1. First check if we need to calculate based on priority at all
    if (!prioritySetsTheDueDateConfig) {
      // returns the date provided by the date-picker or today's date
      const defaultDate = rawDate || moment()
      return defaultDate.toISOString()
    }

    const {
      defaultPriorityValues,
      excludeWeekends,
      allowDefaultsToBeOverridden,
    } = prioritySetsTheDueDateConfig

    // 2. Look up the duration configuration for the selected priority
    const priorityConfig = defaultPriorityValues.find(
      (config) => config.priority === priority,
    )
    const durationInSeconds = priorityConfig?.maxDurationSeconds

    // 3. If raw date is provided by the date picker, check if it is allowed to override the priority due dates
    if (rawDate) {
      // If no priority config found, return original date
      if (!durationInSeconds) {
        return rawDate.toISOString()
      }

      // if priority config is found but allowDefaultsToBeOverridden is true, return the original date
      if (!!durationInSeconds && allowDefaultsToBeOverridden) {
        return rawDate.toISOString()
      }
    }

    // 4. Calculate the due date based on priority duration
    const dueDate = moment()

    // 4a. Simple case: If weekends are included, just add the duration
    if (!excludeWeekends) {
      dueDate.add(durationInSeconds, 'seconds')
      return dueDate.toISOString()
    }

    // 4b. Complex case: Add duration while skipping weekends
    const totalDaysToAdd = Math.floor(durationInSeconds / (24 * 3600))
    const remainingSeconds = durationInSeconds % (24 * 3600)

    // Add business days (including weekend compensation)
    const totalCalendarDays =
      totalDaysToAdd + Math.floor(totalDaysToAdd / 5) * 2
    // Example: if totalDaysToAdd = 7 (business days)
    // totalCalendarDays = 7 + Math.floor(7/5) * 2
    // = 7 + 1 * 2
    // = 9 calendar days (effectively skipping weekends)
    dueDate.add(totalCalendarDays, 'days')

    // Add remaining seconds
    dueDate.add(remainingSeconds, 'seconds')

    // Move to next business day if we land on weekend
    const day = dueDate.day()
    if (day === 6) dueDate.add(2, 'days') // Saturday -> Monday
    if (day === 0) dueDate.add(1, 'days') // Sunday -> Monday

    return dueDate.toISOString()
  }

  public static statusString(status: TaskStatus): string {
    switch (status) {
      case 'DRAFT':
        return statuses.draft.label
      case 'IN_PROGRESS':
        return statuses.inProgress.label
      case 'AWAITING_APPROVAL':
        return statuses.approval.label
      case 'DONE':
        return statuses.completed.label
      case 'CANCELLED':
        return statuses.cancelled.label
      default:
        return status
    }
  }

  public static getStatusChipColor(status: TaskStatus): StatusColor {
    switch (status) {
      case 'DRAFT':
        return statuses.draft.color
      case 'IN_PROGRESS':
        return statuses.inProgress.color
      case 'AWAITING_APPROVAL':
        return statuses.approval.color
      case 'DONE':
        return statuses.completed.color
      case 'CANCELLED':
        return statuses.cancelled.color
      default:
        return 'resolved'
    }
  }

  public hasPermission(action: TaskPermittedActions): boolean {
    return this.permittedActions.includes(action)
  }

  public isNextStepApproval(): boolean {
    return this.nextStep?.type === 'APPROVAL'
  }

  public isNextStepWork(): boolean {
    return this.nextStep?.type === 'WORK'
  }

  // If there is no next step, then the task is done
  public isNextStepDone(): boolean {
    return !this.nextStep
  }

  public isInProgress(): boolean {
    return this.status === 'IN_PROGRESS'
  }

  public isAwaitingApproval(): boolean {
    return this.status === 'AWAITING_APPROVAL'
  }

  public isCancelled(): boolean {
    return this.status === 'CANCELLED'
  }

  public isDone(): boolean {
    return this.status === 'DONE'
  }

  public getCurrentStepQuestions(): TemplateForm {
    return this.currentStep?.stepQuestions
  }

  public hasCurrentStepQuestions(): boolean {
    return this.currentStep?.stepQuestions?.questions?.length > 0
  }

  public hasCurrentStepQuestionsAnswers(): boolean {
    return (
      this.submittedSteps.steps.find((step) => step.id === this.currentStep?.id)
        ?.stepAnswers?.answers?.length > 0
    )
  }

  public hasOnlyOneWorkStep(): boolean {
    return (
      this.template?.currentVersion.steps?.taskSteps?.filter(
        (step) => step.type === 'WORK',
      )?.length === 1
    )
  }

  public getPreviousStep(): TaskStep {
    const stepsLength = this.submittedSteps?.steps?.length

    return this.submittedSteps?.steps?.[stepsLength - 1]
  }

  public getCurrentStepQuestionsAnswers(): TemplateFormAnswers {
    return this.submittedSteps.steps.find(
      (step) => step.id === this.currentStep?.id,
    )?.stepAnswers
  }

  public getNextStepQuestions(): TemplateForm {
    return this.nextStep?.stepQuestions
  }

  public hasNextStepQuestions(): boolean {
    return this.nextStep?.stepQuestions?.questions?.length > 0
  }

  public hasRequestedChanges(): boolean {
    return !!this.lastRequestedChange && !this.lastRequestedChange?.addressedBy
  }

  public hasRequestedChangesComment(): boolean {
    return !!this.lastRequestedChange?.comment
  }

  public hasAddressedChanges(): boolean {
    return !!this.lastRequestedChange && !!this.lastRequestedChange?.addressedBy
  }

  public getAssignee(): User {
    return this.assignee
  }

  public getLastStep(): TaskStep {
    return this.submittedSteps?.steps?.[this.submittedSteps?.steps?.length - 1]
  }

  public isOverdue(): boolean {
    return (
      moment().isAfter(this.dueDate) &&
      !['DONE', 'CANCELLED'].includes(this.status)
    )
  }

  public getPreviousStepSubmittedBy(): User {
    const stepsLength = this.submittedSteps?.steps?.length

    // If there are no steps, then the previous step is the createdBy
    if (stepsLength === 0) {
      return this.createdBy
    }

    const previousStep = this.submittedSteps?.steps?.[stepsLength - 1]

    return previousStep?.submittedBy
  }
}
